import React from "react";
import {
  Box,
  Paper,
  makeStyles,
  Typography,
  Divider,
  Grid,
  Tooltip
} from "@material-ui/core";
import InfiniteScroll from "react-infinite-scroll-component";
import Skeleton from "@material-ui/lab/Skeleton";
import LoadingComponent from "components/loading";
import DefaultImage from "assets/img/defaultPhoto.png";
import RefreshTable from "components/table/refreshTable";
import { getLang } from "app/feature/constants";

const useStyle = makeStyles((theme) => ({
  listItemRoot: {
    padding: theme.spacing(0, 2),
    borderTop: "1px solid",
    borderTopColor: theme.palette.divider,
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer"
  },
  employeeContainer: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    overflowX: "hidden"
  },
  employeeList: {
    height: 200,
    overflowY: "auto"
  },
  employeeThumbnail: {
    height: 50,
    width: 50,
    minWidth: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& img": {
      height: "100%",
      minHeight: "100%",
      minWidth: "100%",
      objectFit: "cover",
      flexShrink: 0,
      borderRadius: "50%"
    }
  },
  employeeName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: theme.typography.fontWeightMedium,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 220,
    },
  },
  dot: {
    height: 10,
    width: 10,
    borderRadius: "50%",
    backgroundColor: "#4BCB0F",
    margin: "4px"
  },
  dotList: {
    height: 4,
    width: 4,
    borderRadius: "50%",
    backgroundColor: "#899498",
    margin: "4px"
  },
  placeContainer: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 150,
    },
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "center"
  }
}));

export default function OnlineEmployeePanelComponent({
  handleView,
  isFetching,
  fetchMoreData,
  hasMoretoLoad,
  onlineEmployeeData,
  isLoadingOnlineError,
  handleReload,
  lang
}) {
  const classes = useStyle();

  function EmployeeList({ thumbnailScr, employeeName, position, place, id }) {
    const classes = useStyle();

    return (
      <Box className={classes.listItemRoot} onClick={() => handleView(id)}>
        <Box className={classes.employeeContainer}>
          <Box className={classes.employeeThumbnail}>
            {thumbnailScr === null ? (
              <img src={DefaultImage} alt="default profile" />
            ) : (
              <img src={thumbnailScr.url} alt={employeeName} />
            )}
          </Box>
          <Box ml={2} style={{ width: "100%" }} overflow="hidden">
            <Tooltip title={employeeName}>
              <Typography
                variant="body2"
                color="primary"
                className={classes.employeeName}
              >
                {employeeName}
              </Typography>
            </Tooltip>

            <div style={{ display: "flex" }}>
              <Box style={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "flex", whiteSpace: "nowrap" }}
                >
                  {position === "owner" ? getLang(lang, 'label.OWNER') : position}&nbsp;
                </Typography>
                <Box className={classes.dotList} />
              </Box>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.placeContainer}
                >
                  {place.map((field) => field.name).join(", ")}
                </Typography>
              </span>
            </div>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Paper elevation={0} className={classes.root}>
      <Box p={2}>
        <Grid container>
          <Grid item xs={9} sm={9} md={9} style={{ width: "100%" }}>
            <Box style={{ display: "flex", alignItems: "center" }}>
              <Box className={classes.dot} />
              <Typography
                variant="body2"
                style={{ fontWeight: "bold", marginLeft: "8px" }}
              >
                {getLang(lang, "label.ONLINE_EMPLOYEE")}
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs={3}
            sm={3}
            md={3}
            style={{ width: "100%", textAlign: "end" }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: "bold",
                marginLeft: "8px",
                color: "#4BCB0F"
              }}
            >
                {getLang(lang, "label.LIVE")}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Divider />

      {onlineEmployeeData.length > 0 || isFetching ? (
        <React.Fragment>
          {isFetching ? (
            <>
              {[1, 2, 3].map((v, index) => (
                <Box
                  p={1}
                  display="grid"
                  gridTemplateColumns="1fr 3fr"
                  key={index}
                >
                  <Skeleton variant="circle" height={50} width={50} />

                  <div style={{ width: "100%" }}>
                    <Skeleton width={"100%"} height={30} />
                    <Skeleton width={"100%"} height={30} />
                  </div>
                </Box>
              ))}
            </>
          ) : !isLoadingOnlineError && (
            <InfiniteScroll
              dataLength={onlineEmployeeData.length}
              next={fetchMoreData}
              hasMore={hasMoretoLoad}
              loader={<LoadingComponent height={50} />}
              height={200}
              style={{ position: "relative" }}
            >
              <Box className={classes.employeeList}>
                {onlineEmployeeData.map((employee, index) => (
                  <EmployeeList
                    key={index}
                    thumbnailScr={employee.imageProfileUrl}
                    employeeName={employee.employeeName}
                    position={employee.userGroup}
                    place={employee.nodeName}
                    id={employee.employeeId}
                  />
                ))}
              </Box>
            </InfiniteScroll>
          )}
        </React.Fragment>
      ) : !isLoadingOnlineError && (
        <Box p={2} py={9} className={classes.emptyContainer}>
          <Typography variant="body1">
            {getLang(lang, "paragraph.NO_ONLINE_EMPLOYEE")}
          </Typography>
        </Box>
      )}

      {!isFetching && isLoadingOnlineError && (
        <RefreshTable handleReload={handleReload} />
      )}

    </Paper>
  );
}
